import { useMediaQuery, IconButton } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useMemo, useState } from 'react';
import { Page, FullTable as Table, MobileTable } from '../../components';
import { EmployeesAPI } from '../../fetch/employee';
import { useToastContext } from '../../hooks';
import { sortable } from '../../models';
import { IEmployee, IEmployeeRole, ILookupType } from '../../models/employee';
import { EmployeeEditorModal } from './EditorModal';
import { EmployeeProfileFIlters } from './EmployeeProfileFilters';
import { Edit } from '@mui/icons-material';
import { IAgentCommissionAPI } from '../../fetch';

export const STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

const DEFAULT_FILTERS = { IsActive: true };
export const EmployeeProfileManagementList = () => {
  const { showToast } = useToastContext();

  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [loadingEmployeeRoles, setLoadingEmployeeRoles] = useState(false);
  const [employeeRoles, setEmployeeRoles] = useState<IEmployeeRole[]>([]);
  const [page, setPage] = useState(0);
  const [perPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [selectedSort, setSelectedSort] = useState<string>('1');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>(STATUS.ACTIVE);
  const [sortDirection, setSortDirection] = useState<{
    EmployeeNumber?: sortable;
    FirstName?: sortable;
    LastName?: sortable;
    Title?: sortable;
    Department?: sortable;
    Office?: sortable;
  }>({
    EmployeeNumber: 'Asc',
  });

  const [searchValue, setSearchValue] = useState<string>('');
  const [filters, setFilters] = useState<{
    IsActive?: boolean;
    Role?: string;
  }>(DEFAULT_FILTERS);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const handleClose = () => {
    setSelectedEmployeeId('');
  };

  const handleCloseAfterSave = () => {
    setSelectedEmployeeId('');
    fetchEmployees();
  };

  const fetchEmployees = async () => {
    setLoadingEmployees(true);

    try {
      const employees = await EmployeesAPI.fetchListOfEmployees({
        sortBy: selectedSort,
        // @ts-ignore
        sortDirection: sortDirection[selectedSort],
        page: page + 1,
        perPage,
        Name: searchValue,
        ...filters,
      });
      setEmployees(employees.records);
      setRecordCount(employees.totalRecordCount);
    } catch (error: any) {
      showToast('error', error?.detail || 'Error trying to retrieve Employees. Please try again.');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchEmployeeRoles = async () => {
    setLoadingEmployeeRoles(true);
    try {
      const roles = await EmployeesAPI.fetchRoles();
      setEmployeeRoles(roles);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Roles. Please try again.'
      );
    } finally {
      setLoadingEmployeeRoles(false);
    }
  };

  const [loadingSubsidiaries, setLoadingSubsidiaries] = useState(false);
  const [subsidiaries, setSubsidiaries] = useState<ILookupType[]>([]);
  const fetchSubsidiaries = async () => {
    setLoadingSubsidiaries(true);
    try {
      const subsidiaries = await EmployeesAPI.fetchSubsidiaries();
      setSubsidiaries(subsidiaries);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Susidiaries. Please try again.'
      );
    } finally {
      setLoadingSubsidiaries(false);
    }
  };

  const [loadingPrimaryBusinessUnitOverride, setLoadingPrimaryBusinessUnitOverride] =
    useState(false);
  const [primaryBusinessUnitOverride, setPrimaryBusinessUnitOverride] = useState<ILookupType[]>([]);
  const fetchPrimaryBusinessUnitOverride = async () => {
    setLoadingPrimaryBusinessUnitOverride(true);
    try {
      const businessUnitTypes = await EmployeesAPI.fetchBusinessUnitTypes();
      setPrimaryBusinessUnitOverride(businessUnitTypes);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail ||
          'Error trying to retrieve Primary Business Unit Overrides. Please try again.'
      );
    } finally {
      setLoadingPrimaryBusinessUnitOverride(false);
    }
  };

  const [loadingdepartmentOverride, setLoadingdepartmentOverride] =
    useState(false);
  const [departmentOverride, setdepartmentOverride] = useState<ILookupType[]>([]);
  const fetchdepartmentOverride = async () => {
    setLoadingdepartmentOverride(true);
    try {
      const departments = await EmployeesAPI.fetchDepartments();
      setdepartmentOverride(departments);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail ||
          'Error trying to retrieve Department Overrides. Please try again.'
      );
    } finally {
      setLoadingdepartmentOverride(false);
    }
  };

  const [loadingProductTypeOverride, setLoadingProductTypeOverride] = useState(false);
  const [productTypeOverride, setProductTypeOverride] = useState<ILookupType[]>([]);
  const fetchProductTypeOverride = async () => {
    setLoadingProductTypeOverride(true);
    try {
      const productTypeOverrides = await EmployeesAPI.fetchProductTypes();
      setProductTypeOverride(productTypeOverrides);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Product Types for Override. Please try again.'
      );
    } finally {
      setLoadingProductTypeOverride(false);
    }
  };

  const [loadingOffices, setLoadingOffices] = useState(false);
  const [offices, setOffices] = useState<ILookupType[]>([]);
  const fetchOffices = async () => {
    setLoadingOffices(true);
    try {
      const offices = await EmployeesAPI.fetchOffices();
      setOffices(offices);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Offices. Please try again.'
      );
    } finally {
      setLoadingOffices(false);
    }
  };

  const [loadingActivityTypes, setLoadingActivityTypes] = useState(false);
  const [activityTypes, setActivityTypes] = useState<ILookupType[]>([]);
  const fetchActivityTypes = async () => {
    setLoadingActivityTypes(true);
    try {
      const activityTypes = await EmployeesAPI.fetchActivityTypes();
      setActivityTypes(activityTypes);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Activity Types. Please try again.'
      );
    } finally {
      setLoadingActivityTypes(false);
    }
  };

  const [loadingSpecialtyTypes, setLoadingSpecialtyTypes] = useState(false);
  const [specialtyTypes, setSpecialtyTypes] = useState<ILookupType[]>([]);
  const fetchSpecialtyTypes = async () => {
    setLoadingSpecialtyTypes(true);
    try {
      const activityTypes = await EmployeesAPI.fetchSpecialtyTypes();
      setSpecialtyTypes(activityTypes);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Specialty Types. Please try again.'
      );
    } finally {
      setLoadingSpecialtyTypes(false);
    }
  };

  const [loadingProductSpecialties, setLoadingProductSpecialties] = useState(false);
  const [productSpecialties, setProductSpecialties] = useState<ILookupType[]>([]);
  const fetchProductSpecialties = async () => {
    setLoadingProductSpecialties(true);
    try {
      const productSpecialties = await EmployeesAPI.fetchProductSpecialties();
      setProductSpecialties(productSpecialties);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Product Specialties. Please try again.'
      );
    } finally {
      setLoadingProductSpecialties(false);
    }
  };

  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teams, setTeams] = useState<ILookupType[]>([]);
  const fetchTeams = async () => {
    setLoadingTeams(true);
    try {
      const teams = await EmployeesAPI.fetchTeams();
      setTeams(teams);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee teams. Please try again.'
      );
    } finally {
      setLoadingTeams(false);
    }
  };

  const [commissionTables, setCommissionTables] = useState<ILookupType[]>([]);
  const fetchCommissionTables = async () => {
    setLoadingActivityTypes(true);
    try {
      const commissionTables = await IAgentCommissionAPI.getTablesLookup();
      setCommissionTables(commissionTables);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Activity Types. Please try again.'
      );
    } finally {
      setLoadingActivityTypes(false);
    }
  };

  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [bankAccounts, setbankAccounts] = useState<ILookupType[]>([]);
  const fetchBankAccounts = async () => {
    setLoadingBankAccounts(true);
    try {
      const bankAccounts = await EmployeesAPI.fetchBankAccounts();
      setbankAccounts(bankAccounts);
    } catch (error: any) {
      showToast(
        'error',
        error?.detail || 'Error trying to retrieve Employee Bank Accounts. Please try again.'
      );
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    // Fetches for Modal
    fetchEmployeeRoles();
    fetchSubsidiaries();
    fetchOffices();
    fetchActivityTypes();
    fetchSpecialtyTypes();
    fetchCommissionTables();
    fetchProductSpecialties();
    fetchTeams();
    fetchPrimaryBusinessUnitOverride();
    fetchdepartmentOverride();
    fetchProductTypeOverride();
    fetchBankAccounts();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ModalDropdownValuesLoading =
    loadingSubsidiaries ||
    loadingOffices ||
    loadingActivityTypes ||
    loadingSpecialtyTypes ||
    loadingProductSpecialties ||
    loadingTeams ||
    loadingPrimaryBusinessUnitOverride ||
    loadingdepartmentOverride ||
    loadingProductTypeOverride ||
    loadingBankAccounts;

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, selectedSort, sortDirection, filters]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      // @ts-ignore
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc',
    });
  };

  const columns = useMemo(() => {
    return [
      {
        accessor: 'employeeId',
        Header: 'Employee Number',
        isServerSorted: selectedSort === 'EmployeeNumber',
        isServerSortedDesc: sortDirection.EmployeeNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('EmployeeNumber'),
      },
      {
        accessor: 'firstName',
        Header: 'First Name',
        isServerSorted: selectedSort === 'FirstName',
        isServerSortedDesc: sortDirection.FirstName === 'Desc',
        handleClickColumn: () => handleClickColumn('FirstName'),
      },
      {
        accessor: 'lastName',
        Header: 'Last Name',
        isServerSorted: selectedSort === 'LastName',
        isServerSortedDesc: sortDirection.LastName === 'Desc',
        handleClickColumn: () => handleClickColumn('LastName'),
      },
      {
        accessor: 'title',
        Header: 'Title',
        isServerSorted: selectedSort === 'Title',
        isServerSortedDesc: sortDirection.Title === 'Desc',
        handleClickColumn: () => handleClickColumn('Title'),
      },
      {
        accessor: 'department',
        Header: 'Department',
        isServerSorted: selectedSort === 'Department',
        isServerSortedDesc: sortDirection.Department === 'Desc',
        handleClickColumn: () => handleClickColumn('Department'),
      },
      {
        accessor: 'office',
        Header: 'Office',
        isServerSorted: selectedSort === 'Office',
        isServerSortedDesc: sortDirection.Office === 'Desc',
        handleClickColumn: () => handleClickColumn('Office'),
      },
      {
        accessor: 'roles',
        Header: 'Roles',
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <span>
              {original.roles
                .map(
                  (role: string | number) =>
                    employeeRoles.find(empRole => empRole.value === role)?.description
                )
                .join(', ')}
            </span>
          );
        },
      },
      {
        accessor: 'actions',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <IconButton onClick={() => setSelectedEmployeeId(original.employeeId)}>
              <Edit />
            </IconButton>
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeRoles, page, perPage, selectedSort, sortDirection, filters, searchValue]);

  return (
    <Page title="Employees">
      <Box>
        <Box>
          <EmployeeProfileFIlters
            isLoading={loadingEmployees || loadingEmployeeRoles}
            applyFilters={(clearFilters?: boolean) => {
              if (clearFilters) {
                setFilters(DEFAULT_FILTERS);
                setSearchValue('');
              } else {
                setPage(0);
                setFilters({
                  ...filters,
                  Role: selectedRole || undefined,
                  IsActive: selectedStatus === STATUS.ACTIVE ? true : false,
                });
              }
            }}
            roles={employeeRoles}
            selectedRole={selectedRole}
            setSelectedRole={setSelectedRole}
            setSearchValue={setSearchValue}
            searchValue={searchValue}
            handleSearch={(val: string) => {
              setPage(0);
              setSearchValue(val);
            }}
            hasAppliedFilters={hasAppliedFilters}
            setHasAppliedFilters={setHasAppliedFilters}
            statuses={[
              { value: STATUS.ACTIVE, description: 'Active', shorthand: 'Active' },
              { value: STATUS.INACTIVE, description: 'Inactive', shorthand: 'Inactive' },
            ]}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
          />
          <Table
            columns={columns}
            data={employees}
            isLoading={loadingEmployees || loadingEmployeeRoles}
            stickyHeader
            serverPage={page}
            serverRecordCount={recordCount}
            serverPerPage={perPage}
            handlePage={setPage}
            handleRowsPerPage={setRowsPerPage}
            ResponsiveComponent={isMobile ? MobileTable : undefined}
            mobileProps={{
              fields: columns
                .filter(col => col.Header)
                .map(col => ({
                  name: col.Header,
                  accessor: col.accessor,
                  Cell: col?.Cell,
                })),
              mobileAccordionLabel: (original: any) =>
                `${original?.firstName ?? ''} ${original?.lastName ?? ''}`,
              handleEdit: (original: any) => {
                setSelectedEmployeeId(original.employeeId);
              },
            }}
          />
        </Box>
      </Box>
      <EmployeeEditorModal
        open={!!selectedEmployeeId}
        onCloseAfterSave={() => handleCloseAfterSave()}
        onClose={() => handleClose()}
        employeeId={selectedEmployeeId}
        areModalDropdownValuesLoading={ModalDropdownValuesLoading}
        subsidiaries={subsidiaries}
        bankAccounts={bankAccounts}
        roles={employeeRoles}
        offices={offices}
        primaryBusinessUnitOverride={primaryBusinessUnitOverride}
        departmentOverride={departmentOverride}
        productTypeOverride={productTypeOverride}
        activityTypes={activityTypes}
        specialtyTypes={specialtyTypes}
        productSpecialties={productSpecialties}
        teams={teams}
        commissionTables={commissionTables}
      />
    </Page>
  );
};
